/**
 * AsignarBotesPage — Asignar embarcaciones a cada salida.
 * Lista de salidas futuras con sus botes; click → diálogo para asignar/quitar.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Box, Typography, Button, Paper, Stack, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, FormGroup, FormControlLabel, Checkbox,
  TextField, Alert, IconButton,
} from '@mui/material';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EmptyState from '../common/EmptyState';
import useToast from '../common/useToast';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';
function getEmpresaId() { try { return JSON.parse(localStorage.getItem('user') || '{}').company_id || '1'; } catch { return '1'; } }
function authHeaders() { const t = localStorage.getItem('token'); return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) }; }
function fmtFecha(s) { if (!s) return '—'; try { return new Date(s).toLocaleDateString('es-CO', { weekday: 'short', day: 'numeric', month: 'short' }); } catch { return s; } }

export default function AsignarBotesPage() {
  const empresaId = getEmpresaId();
  const { refreshCounts } = useOutletContext() || {};
  const { toast, ToastContainer } = useToast();
  const [salidas, setSalidas] = useState([]);
  const [embarcaciones, setEmbarcaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogSalida, setDialogSalida] = useState(null);
  const [seleccionados, setSeleccionados] = useState([]);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, eRes] = await Promise.all([
        fetch(`${API_BASE}/turismo/salidas?empresa_id=${empresaId}`, { headers: authHeaders() }),
        fetch(`${API_BASE}/turismo/embarcaciones?empresa_id=${empresaId}`, { headers: authHeaders() }),
      ]);
      const sData = await sRes.json();
      const list = Array.isArray(sData) ? sData : (sData.salidas || []);
      const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
      // Adapter: el endpoint /salidas devuelve `embarcaciones_asignadas`
      // (con `id` y `embarcacion_id`), pero este componente espera el shape
      // `botes` (con `asignacion_id` y `embarcacion_id`). Mapeamos al cargar.
      const futuras = list
        .filter(s => s.fecha && new Date(s.fecha) >= hoy && s.estado !== 'cancelada')
        .map(s => ({
          ...s,
          botes: (s.embarcaciones_asignadas || []).map(a => ({
            asignacion_id: a.id,
            embarcacion_id: a.embarcacion_id,
            embarcacion_nombre: a.embarcacion_nombre,
            capacidad: a.capacidad_asignada,
          })),
        }));
      setSalidas(futuras);
      setEmbarcaciones(await eRes.json());
    } catch (e) { toast.error('Error cargando'); }
    setLoading(false);
  }, [empresaId]); // eslint-disable-line
  useEffect(() => { cargar(); }, [cargar]);

  // Salidas SIN botes asignados arriba
  const ordenadas = useMemo(() => {
    return [...salidas].sort((a, b) => {
      const na = (a.botes || []).length, nb = (b.botes || []).length;
      if (na === 0 && nb > 0) return -1;
      if (nb === 0 && na > 0) return 1;
      return new Date(a.fecha) - new Date(b.fecha);
    });
  }, [salidas]);

  const abrirAsignar = (salida) => {
    setDialogSalida(salida);
    setSeleccionados((salida.botes || []).map(b => b.embarcacion_id));
  };

  const guardarAsignacion = async () => {
    if (!dialogSalida) return;
    const errores = [];   // recolecta errores 400 individuales — fetch NO lanza en 4xx, hay que chequear res.ok
    let exitos = 0;
    try {
      const actuales = (dialogSalida.botes || []).map(b => ({ asignacion_id: b.asignacion_id, embarcacion_id: b.embarcacion_id }));

      // Eliminar los que ya no están
      const aQuitar = actuales.filter(a => !seleccionados.includes(a.embarcacion_id));
      for (const a of aQuitar) {
        const res = await fetch(`${API_BASE}/turismo/salidas/embarcaciones/${a.asignacion_id}`,
          { method: 'DELETE', headers: authHeaders() });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          errores.push(`No se pudo quitar bote #${a.embarcacion_id}: ${body.detail || res.statusText}`);
        } else {
          exitos++;
        }
      }

      // Agregar los nuevos
      const yaAsignados = actuales.map(a => a.embarcacion_id);
      const aAgregar = seleccionados.filter(id => !yaAsignados.includes(id));
      for (const id of aAgregar) {
        const res = await fetch(`${API_BASE}/turismo/salidas/${dialogSalida.id}/embarcaciones`,
          { method: 'POST', headers: authHeaders(), body: JSON.stringify({ embarcacion_id: id }) });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          const nombreBote = (embarcaciones.find(b => b.id === id) || {}).nombre || `#${id}`;
          errores.push(`${nombreBote}: ${body.detail || res.statusText}`);
        } else {
          exitos++;
        }
      }

      // Reportar resultado al operador (clave: NO claimar éxito si hubo errores)
      if (errores.length > 0 && exitos === 0) {
        toast.error(errores.join(' · '));
      } else if (errores.length > 0) {
        toast.warning(`${exitos} cambios aplicados, pero hubo errores: ${errores.join(' · ')}`);
      } else {
        toast.success('Botes actualizados');
      }
      setDialogSalida(null);
      cargar();
      refreshCounts?.();
    } catch (e) {
      toast.error('Error de red actualizando los botes');
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>Asignación de botes</Typography>
        <Typography sx={{ fontSize: 12, color: '#64748b' }}>
          Define qué embarcaciones operan en cada salida. Las salidas sin botes aparecen arriba.
        </Typography>
      </Box>

      {ordenadas.length === 0 ? (
        <EmptyState
          icon={<DirectionsBoatIcon sx={{ fontSize: 56, color: '#cbd5e1' }} />}
          title="Sin salidas próximas"
          message="No hay salidas futuras programadas."
        />
      ) : (
        <Stack spacing={1}>
          {ordenadas.map(s => {
            const nBotes = (s.botes || []).length;
            return (
              <Paper key={s.id} sx={{ p: 1.5, borderRadius: 2,
                borderLeft: nBotes === 0 ? '4px solid #f59e0b' : '4px solid #16a34a' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                      {fmtFecha(s.fecha)} · {s.hora_salida?.slice(0, 5) || '—'} · {s.paquete_nombre}
                    </Typography>
                    <Stack direction="row" spacing={0.5} mt={0.5}>
                      {nBotes === 0 ? (
                        <Chip size="small" label="⚠ Sin botes" sx={{ bgcolor: '#fef3c7', color: '#92400e', fontSize: 10, height: 20 }} />
                      ) : (
                        (s.botes || []).map(b => (
                          <Chip key={b.embarcacion_id} size="small"
                            label={`🚤 ${b.embarcacion_nombre || 'Bote'} (${b.capacidad || 0})`}
                            sx={{ bgcolor: '#e0f2fe', color: '#075985', fontSize: 10, height: 20 }} />
                        ))
                      )}
                    </Stack>
                  </Box>
                  <Button variant="outlined" size="small" onClick={() => abrirAsignar(s)}
                    sx={{ textTransform: 'none' }}>
                    {nBotes === 0 ? 'Asignar botes' : 'Editar'}
                  </Button>
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}

      <Dialog open={!!dialogSalida} onClose={() => setDialogSalida(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Asignar botes</DialogTitle>
        <DialogContent>
          {dialogSalida && (
            <>
              <Alert severity="info" sx={{ mb: 2, fontSize: 12 }}>
                {fmtFecha(dialogSalida.fecha)} · {dialogSalida.paquete_nombre}
              </Alert>
              <FormGroup>
                {embarcaciones.map(e => (
                  <FormControlLabel key={e.id}
                    control={
                      <Checkbox size="small" checked={seleccionados.includes(e.id)}
                        onChange={ev => {
                          setSeleccionados(prev => ev.target.checked
                            ? [...prev, e.id]
                            : prev.filter(x => x !== e.id));
                        }} />
                    }
                    label={
                      <Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                          🚤 {e.nombre} {e.tipo ? `(${e.tipo})` : ''}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>
                          Capacidad: {e.capacidad_total || 0} pax
                        </Typography>
                      </Box>
                    } />
                ))}
              </FormGroup>
              {embarcaciones.length === 0 && (
                <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>
                  No hay embarcaciones registradas. Créalas en Catálogo → Embarcaciones.
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogSalida(null)}>Cancelar</Button>
          <Button variant="contained" onClick={guardarAsignacion} disableElevation>Guardar</Button>
        </DialogActions>
      </Dialog>

      <ToastContainer />
    </Box>
  );
}
